import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { LoadingButton } from '@/components/shared/loading-button';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import type { App } from '@/lib/types';

/** Typing the name to confirm: deleting stops every push for the app at once. */
export function DeleteAppDialog({ app, onClose, onDeleted }: { app: App | null; onClose: () => void; onDeleted?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const del = useMutation({
    mutationFn: (id: string) => api(`/admin/v1/apps/${id}`, { method: 'DELETE' }),
    onSuccess: (_r, id) => {
      toast(`Deleted ${app?.name ?? 'application'}`);
      // Drop the app's cached detail so nothing refetches it into a 404 after we leave.
      qc.removeQueries({ queryKey: ['app', id] });
      void qc.invalidateQueries({ queryKey: ['apps'] });
      close();
      onDeleted?.();
    },
  });
  const close = () => { setTyped(''); del.reset(); onClose(); };

  return (
    <Dialog open={!!app} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {app?.name}?</DialogTitle>
          <DialogDescription>
            This will immediately stop all notifications, invalidate all associated API keys, and prevent any queued
            notifications from being sent. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <label className="grid gap-2 text-sm">
          <span>Type <b className="font-mono">{app?.name}</b> to confirm</span>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" placeholder={app?.name} />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <LoadingButton variant="destructive" disabled={typed !== app?.name} loading={del.isPending} onClick={() => app && del.mutate(app.id)}>
            <Trash2 /> Delete application
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
